import React, { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Heading from 'components/Luxkit/Typography/Heading'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { isAvailableForPurchase, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import MultipleRoomsBookingModalPriceDetails from './MultipleRoomsBookingModalPriceDetails'
import MultipleRoomsInclusions from './MultipleRoomsInclusions'
import LoyaltyPoints from 'loyalty/components/LoyaltyPoints'
import { generateLoyaltyPointsCalculatorHotelOptions, generateLoyaltyPointsCalculatorBundledFlightOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'

const PackageOption = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${rem(16)};
  border-top: 1px solid ${props => props.theme.palette.neutral.default.five};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 0.6fr;
    grid-gap: ${rem(24)};
  }
`

const DetailsSection = styled.div`
  display: flex;
  flex-direction: column;

  grid-column: 1;
  grid-row: 1;
  padding: ${rem(16)} ${rem(24)};

  > * + * {
    margin-top: ${rem(8)};
  }

  ${mediaQueryUp.desktop} {
    grid-column: 1;
    grid-row: 1;
    padding: ${rem(16)};
  }
`

const PriceSection = styled.div`
  display: flex;
  flex-direction: column;

  grid-column: 1;
  grid-row: 2;
  padding: 0 ${rem(24)} ${rem(16)};

  ${mediaQueryUp.tablet} {
    align-items: flex-start;
    grid-column: 1 / span 2;
    padding: ${rem(16)} ${rem(24)};
  }

  ${mediaQueryUp.desktop} {
    grid-column: 2;
    grid-row: 1;
    padding: ${rem(16)};
  }
`

interface MappedProps {
  checkIn?: string;
  availableForPurchase?: boolean;

}
interface Props extends MappedProps {
  offer: App.Offer;
  packageView: OfferPackageOptionView;
  selectedPackageUniqueKey?: string;
  room: App.RoomOccupants;
  onSelect: (packageOption: App.Package) => void;
  isFlightsSelected: boolean;
  isFlightsEnabled: boolean;
  closeRoomsModal: () => void;
  availableForPurchase?: boolean;
}

function MultipleRoomsBookingModalOption(props: Props) {
  const {
    offer,
    packageView,
    checkIn,
    selectedPackageUniqueKey,
    room,
    onSelect,
    isFlightsSelected,
    isFlightsEnabled,
    closeRoomsModal,
    availableForPurchase,
  } = props

  const pkg = packageView.package

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView }),
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, { packageView, skip: !isFlightsSelected }),
  ], [offer, packageView, isFlightsSelected])

  return (
    <PackageOption data-testid="package-option">
      <DetailsSection>
        <div>
          <Heading variant="heading6">{pkg.name}</Heading>
          {availableForPurchase && <FlashCancellationPolicies
            checkInDate={checkIn}
            propertyTimezoneOffset={offer.property?.timezoneOffset}
            cancellationPolicy={pkg.roomRate?.cancellationPolicy}
          />}
        </div>
        <LoyaltyPoints
          calculationRequests={pointsEarnCalculationRequests}
          calculationType={(!checkIn || isFlightsSelected) ? 'estimate' : 'exact'}
        />
        <MultipleRoomsInclusions
          offer={offer}
          packageOption={packageView}
        />
      </DetailsSection>
      <PriceSection>
        <MultipleRoomsBookingModalPriceDetails
          room={room}
          packageView={packageView}
          isSelected={selectedPackageUniqueKey === pkg.uniqueKey}
          onSelect={onSelect}
          isFlightsSelected={isFlightsSelected}
          isFlightsEnabled={isFlightsEnabled}
          closeRoomsModal={closeRoomsModal}
        />
      </PriceSection>
    </PackageOption>
  )
}

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  checkIn: state.checkInDate,
  availableForPurchase: isAvailableForPurchase(state, appState),
}), MultipleRoomsBookingModalOption)
